import { api } from "@/lib/api";
import type { UserRole } from "@/lib/auth";
import type { Pagination } from "@/lib/types";

/** ユーザー（API の UserResponse に対応）。 */
export interface AppUser {
  user_id: string;
  email: string;
  name: string | null;
  disabled: boolean;
  is_admin: boolean;
  role: UserRole;
  /** 仮パスワードのまま。true なら本人がまだパスワードを設定していない。 */
  must_change_password: boolean;
  /** 仮パスワードの有効期限。過ぎるとログインできず、管理者が再発行する。 */
  temp_password_expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserListResponse {
  items: AppUser[];
  pagination: Pagination;
}

export interface UserListParams {
  keyword?: string;
  sortKey?: string;
  ascending?: boolean;
  limit: number;
  offset: number;
}

export function listUsers(params?: Partial<UserListParams>): Promise<UserListResponse> {
  const query = new URLSearchParams({
    limit: String(params?.limit ?? 500),
    offset: String(params?.offset ?? 0),
  });
  if (params?.keyword) query.set("keyword", params.keyword);
  if (params?.sortKey) query.set("sort_key", params.sortKey);
  if (params?.ascending != null) query.set("ascending", String(params.ascending));
  return api.get<UserListResponse>(`/users?${query}`);
}

/** 選択肢に出す表示名（表示名が無ければメールアドレス）。 */
export function userLabel(user: AppUser): string {
  return user.name?.trim() || user.email;
}

/** 登録で送れる項目。パスワードは送れない（API が仮パスワードを生成する）。 */
export interface UserCreateInput {
  email: string;
  name?: string | null;
  role?: UserRole;
  disabled?: boolean;
}

/**
 * 登録直後のレスポンス。**仮パスワードはここでしか返らない**ので、
 * 画面で一度だけ見せて控えてもらう。
 */
export interface UserCreatedResponse {
  user: AppUser;
  temporary_password: string;
}

export function createUser(input: UserCreateInput): Promise<UserCreatedResponse> {
  return api.post<UserCreatedResponse>("/users", input);
}

/** 更新で送れる項目。パスワードは含まない（再発行の経路を使う）。 */
export type UserInput = Partial<Pick<AppUser, "email" | "name" | "role" | "disabled">>;

export function updateUser(userId: string, input: UserInput): Promise<AppUser> {
  return api.patch<AppUser>(`/users/${userId}`, input);
}

export function deleteUser(userId: string): Promise<void> {
  return api.delete<void>(`/users/${userId}`);
}

export interface TemporaryPasswordResponse {
  temporary_password: string;
  expires_at: string;
}

/**
 * 仮パスワードを再発行する。**平文はここでしか返らない**。
 *
 * 再発行すると対象ユーザーの既存セッションは全て失効する（漏洩時の対処にも
 * 使うため）。
 */
export function issueTemporaryPassword(
  userId: string,
): Promise<TemporaryPasswordResponse> {
  return api.post<TemporaryPasswordResponse>(`/users/${userId}/temporary-password`);
}
